import { cn } from '@/shared/lib/utils/classnames';
import type { GridBlock as GridBlockType } from '@/shared/types/blocks';
import { Label } from '@/shared/ui/typography';
import { BlockRenderer } from '../BlockRenderer';

interface GridBlockProps extends Omit<GridBlockType, 'type' | 'id'> {
  onToolClick?: (toolId: string) => void;
}

/**
 * GridBlock - 그리드 레이아웃
 *
 * 하위 블록들을 그리드 형태로 배치합니다.
 * scrollable=true 시 가로 스크롤 캐러셀 모드로 동작합니다.
 */
export function GridBlock({
  columns,
  gap = 16,
  items,
  title,
  scrollable = false,
  itemWidth = 280,
  onToolClick,
}: GridBlockProps) {
  // 가로 스크롤 모드
  if (scrollable) {
    return (
      <div className="flex flex-col gap-4">
        {title && (
          <Label size="large" weight="bold">
            {title}
          </Label>
        )}
        <div
          className={cn(
            'flex overflow-x-auto snap-x snap-mandatory scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent',
            'pb-2 -mb-2', // 스크롤바 공간 확보
          )}
          style={{ gap }}
        >
          {items.map((item) => (
            <div key={item.id} className="flex-shrink-0 snap-start" style={{ width: itemWidth }}>
              <BlockRenderer blocks={[item]} onToolClick={onToolClick} className="gap-0" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // 기본 그리드 모드
  const gridStyle =
    columns === 'auto'
      ? { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap }
      : { display: 'grid', gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap };

  return (
    <div className="flex flex-col gap-4">
      {title && (
        <Label size="large" weight="bold">
          {title}
        </Label>
      )}
      <div style={gridStyle}>
        {items.map((item) => (
          <div key={item.id} className="min-w-0">
            <BlockRenderer blocks={[item]} onToolClick={onToolClick} className="gap-0" />
          </div>
        ))}
      </div>
    </div>
  );
}
